import {
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  OutlinedInput,
  Popover,
} from '@material-ui/core';
import { Cancel, Event } from '@material-ui/icons';
import { format } from 'date-fns';
import { equals } from 'ramda';
import React, { useEffect, useState } from 'react';

import { CONSTANTS } from '../../../core/config/constants';
import { CalendarPicker } from './CalendarPicker';
import { CalendarPickerDateRange } from './calendarPicker.types';

type CalendarPickerInputOutput = Date | Date[] | undefined;
type CalendarPickerInputState = CalendarPickerDateRange | undefined;

interface Props {
  onChange?: (value: CalendarPickerInputOutput) => void;
  defaultValue?: CalendarPickerInputOutput;
  isRange?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
}

const CalendarPickerInput: React.FC<Props> = React.memo(
  ({ onChange, isRange = false, defaultValue, minDate, maxDate }) => {
    const classes = useStyles();
    const [state, setState] = useState<CalendarPickerInputState>(parseInput(defaultValue, isRange));
    const [inputValue, setInputValue] = useState(
      defaultValue ? formatTextInputValue(state, isRange) : '',
    );
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const id = anchorEl ? 'popover' : undefined;

    useEffect(() => {
      const parsedDefaultValue = parseInput(defaultValue, isRange);

      if (!equals(state, parsedDefaultValue)) {
        setState(parsedDefaultValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    useEffect(() => {
      setInputValue(formatTextInputValue(state, isRange));

      if (typeof onChange === 'function') {
        onChange(formatOutput(state, isRange) as CalendarPickerInputOutput);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, isRange]);

    const onOpen = (event: React.MouseEvent<any>) => {
      setAnchorEl(event.currentTarget);
    };

    const onClose = () => {
      setAnchorEl(null);
    };

    const onResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setState(undefined);
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDateChange = (date: CalendarPickerDateRange) => {
      if (date && date.startDate && date.endDate) {
        setState(date);
      } else {
        setState(undefined);
      }

      onClose();
    };

    return (
      <>
        <FormControl fullWidth variant="outlined">
          <OutlinedInput
            className={classes.triggerBtn}
            endAdornment={
              <InputAdornment position="end">
                {state && (
                  <IconButton onClick={onResetClick} onMouseDown={onResetMouseDown} size="small">
                    <Cancel className={classes.resetBtn} />
                  </IconButton>
                )}
                <IconButton size="small">
                  <Event />
                </IconButton>
              </InputAdornment>
            }
            readOnly={true}
            value={inputValue}
            aria-describedby={id}
            onClick={onOpen}
          />
        </FormControl>
        <Popover
          id={id}
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <CalendarPicker
            initialDateRange={state as CalendarPickerDateRange}
            open={true}
            onChange={handleDateChange}
            isRange={isRange}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Popover>
      </>
    );

    function parseInput(value?: Date | Date[], isRange?: boolean) {
      if (isRange && Array.isArray(value)) {
        return { startDate: value[0], endDate: value[1] };
      } else if (!isRange && value instanceof Date) {
        return { startDate: value, endDate: value };
      } else {
        return undefined;
      }
    }

    function formatOutput(dates: CalendarPickerInputState, isRange: boolean) {
      if (dates && dates.startDate && dates.endDate) {
        return isRange ? [dates.startDate, dates.endDate] : dates.startDate;
      } else {
        return undefined;
      }
    }

    function formatTextInputValue(dates: CalendarPickerInputState, isRange: boolean): string {
      if (dates && dates.startDate && dates.endDate) {
        return isRange
          ? `${dateToString(dates.startDate)} - ${dateToString(dates.endDate)}`
          : dateToString(dates.startDate);
      } else {
        return '';
      }
    }

    function onResetMouseDown(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();
      event.stopPropagation();
    }

    function dateToString(date?: Date): string {
      return date ? format(date, CONSTANTS.DATE_FORMATS.UI) : '';
    }
  },
);

const useStyles = makeStyles((theme) => ({
  triggerBtn: {
    paddingRight: theme.spacing(4),

    '&:hover': {
      cursor: 'pointer',
      background: theme.palette.common.white,
      borderColor: theme.palette.grey[700],
    },
    '& .MuiSvgIcon-root': {
      fontSize: '22px',
    },
    '& input': {
      cursor: 'pointer',
    },
  },
  resetBtn: {
    color: theme.palette.grey[400],

    '&:hover': {
      color: theme.palette.grey[500],
    },
  },
}));

export default CalendarPickerInput;
